import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useTheme } from '../context/ThemeContext';
import {
    Box, Button, Dialog, DialogTitle, DialogContent, DialogActions,
    Typography, IconButton, Tooltip, useMediaQuery, useTheme as useMuiTheme
} from '@mui/material';
import {
    Dashboard, Assignment, Logout, Warning, Brightness4, Brightness7
} from '@mui/icons-material';

const AdminHeader = () => {
    const navigate = useNavigate();
    const { logout } = useAuth();
    const { isDarkMode, toggleTheme } = useTheme();
    const muiTheme = useMuiTheme();
    const isMobile = useMediaQuery(muiTheme.breakpoints.down('sm'));
    const [logoutDialog, setLogoutDialog] = useState(false);

    const handleLogoutConfirm = () => {
        logout();
        setLogoutDialog(false);
        navigate('/subscription-plans');
    };

    return (
        <>
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 1.5,
                mb: 4,
                p: { xs: 1.5, sm: 2 },
                backgroundColor: isDarkMode ? 'rgba(255, 126, 185, 0.08)' : 'rgba(255, 126, 185, 0.1)',
                borderRadius: '12px',
                border: '2px solid',
                borderColor: 'primary.main',
                transition: 'all 0.3s ease'
            }}>
                {/* Theme Toggle */}
                <Tooltip title={isDarkMode ? 'Светла тема' : 'Темна тема'} arrow>
                    <IconButton
                        onClick={toggleTheme}
                        sx={{
                            bgcolor: 'background.paper',
                            boxShadow: isDarkMode ? '0 4px 15px rgba(255, 126, 185, 0.3)' : '0 4px 15px rgba(0,0,0,0.1)',
                            border: '2px solid',
                            borderColor: isDarkMode ? 'rgba(255, 126, 185, 0.3)' : 'rgba(255, 126, 185, 0.2)',
                            width: 40, height: 40,
                            flexShrink: 0,
                            '&:hover': {
                                bgcolor: 'primary.main', color: 'white',
                                transform: 'scale(1.1) rotate(20deg)',
                                borderColor: 'primary.main',
                            },
                            transition: 'all 0.3s ease',
                        }}
                    >
                        {isDarkMode ? <Brightness7 sx={{ fontSize: 20 }} /> : <Brightness4 sx={{ fontSize: 20 }} />}
                    </IconButton>
                </Tooltip>

                {/* Navigation Buttons */}
                <Box sx={{
                    display: 'flex',
                    gap: { xs: 1, sm: 2 },
                    flexWrap: 'wrap',
                    justifyContent: { xs: 'flex-end', sm: 'flex-end' },
                    flex: 1
                }}>
                    <Button
                        variant="outlined"
                        startIcon={!isMobile ? <Dashboard /> : undefined}
                        onClick={() => navigate('/admin')}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                            color: 'primary.main', borderColor: 'primary.main',
                            fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            px: { xs: 1.5, sm: 2 }, minWidth: 'unset',
                            '&:hover': { borderColor: 'primary.dark', backgroundColor: 'rgba(255, 126, 185, 0.1)', transform: 'translateY(-2px)' },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isMobile ? '🏠' : 'Админ Панел'}
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={!isMobile ? <Assignment /> : undefined}
                        onClick={() => navigate('/admin/programs')}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                            color: isDarkMode ? '#74c0fc' : '#2196F3',
                            borderColor: isDarkMode ? '#74c0fc' : '#2196F3',
                            fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            px: { xs: 1.5, sm: 2 }, minWidth: 'unset',
                            '&:hover': { borderColor: isDarkMode ? '#4dabf7' : '#1976d2', backgroundColor: 'rgba(33, 150, 243, 0.1)', transform: 'translateY(-2px)' },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isMobile ? '📋' : 'Програми'}
                    </Button>

                    <Button
                        variant="outlined"
                        startIcon={!isMobile ? <Logout /> : undefined}
                        onClick={() => setLogoutDialog(true)}
                        size={isMobile ? 'small' : 'medium'}
                        sx={{
                            color: '#ff6b6b', borderColor: '#ff6b6b',
                            fontWeight: 'bold', fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            px: { xs: 1.5, sm: 2 }, minWidth: 'unset',
                            '&:hover': { borderColor: '#ff5252', backgroundColor: 'rgba(255, 107, 107, 0.1)', transform: 'translateY(-2px)' },
                            transition: 'all 0.3s ease'
                        }}
                    >
                        {isMobile ? '🚪' : 'Одјави се'}
                    </Button>
                </Box>
            </Box>

            {/* Logout Dialog */}
            <Dialog
                open={logoutDialog}
                onClose={() => setLogoutDialog(false)}
                maxWidth="sm" fullWidth
                PaperProps={{
                    sx: {
                        borderRadius: '16px',
                        boxShadow: '0 8px 32px rgba(255, 107, 107, 0.2)',
                        border: '2px solid #ff6b6b',
                        bgcolor: 'background.paper'
                    }
                }}
            >
                <DialogTitle sx={{
                    bgcolor: '#ff6b6b', color: 'white', textAlign: 'center',
                    fontWeight: 'bold', display: 'flex', alignItems: 'center',
                    justifyContent: 'center', gap: 1
                }}>
                    <Warning /> Потврда за одјава
                </DialogTitle>

                <DialogContent sx={{ p: 3, textAlign: 'center' }}>
                    <Typography variant="h6" sx={{ color: 'text.primary', mb: 2, fontWeight: 'bold' }}>
                        Дали сте сигурни дека сакате да се одјавите?
                    </Typography>
                    <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                        Ќе бидете пренасочени кон страната за претплати.
                    </Typography>
                </DialogContent>

                <DialogActions sx={{ p: 3, gap: 2, justifyContent: 'center' }}>
                    <Button
                        onClick={() => setLogoutDialog(false)}
                        variant="outlined"
                        sx={{
                            color: isDarkMode ? '#74c0fc' : '#2196F3',
                            borderColor: isDarkMode ? '#74c0fc' : '#2196F3',
                            fontWeight: 'bold', px: 4, borderRadius: '8px',
                        }}
                    >
                        Откажи
                    </Button>
                    <Button
                        onClick={handleLogoutConfirm}
                        variant="contained"
                        startIcon={<Logout />}
                        sx={{
                            bgcolor: '#ff6b6b', color: 'white', fontWeight: 'bold',
                            px: 4, borderRadius: '8px',
                            '&:hover': { bgcolor: '#ff5252', transform: 'translateY(-1px)' },
                        }}
                    >
                        Одјави се
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};

export default AdminHeader;